<template>
	<BaseAutoComplete
		v-bind="{ ...$attrs, ...searchFieldProps(searchTerm, ($attrs.errorMessages ?? $attrs['error-messages'] ?? []) as string | string[]) }"
		clearable
		:loading="isLoading"
		:items="listOfTeamMembers"
		:item-title="getUserDisplayName"
		:return-object="props.returnObject"
		v-model:search="searchTerm"
		:custom-filter="customFilter"
		@focus="() => items.length === 0 && refresh()">
		<template #selection="{ item }">
			<span class="text-truncate">{{ getUserDisplayName(item.raw) }}</span>
		</template>
		<template #item="{ props, item }">
			<v-list-item v-bind="props">
				<v-list-item-subtitle
					><span v-if="item.raw.phone">{{ item.raw.phone }}</span></v-list-item-subtitle
				>
				<v-list-item-subtitle
					><span v-if="item.raw.email">{{ item.raw.email }}</span></v-list-item-subtitle
				>
			</v-list-item>
		</template>
		<template #no-data>
			<v-list-item class="pl-2">
				<v-list-item-title>No users found.</v-list-item-title>
			</v-list-item>
		</template>
		<template v-if="canEditSelected" #append-inner>
			<span @mousedown.stop.prevent @click.stop="handleEditClick">
				<v-tooltip location="top">
					<template #activator="{ props: tooltipProps }">
						<div v-bind="tooltipProps">
							<v-icon color="primary" class="cursor-pointer">icon-pencil-edit-solid</v-icon>
						</div>
					</template>
					<span class="text-no-wrap">Edit user details</span>
				</v-tooltip>
			</span>
		</template>
	</BaseAutoComplete>
	<EditUserDialog ref="editUserDialog" />
</template>

<script setup lang="ts">
	import { searchFieldProps } from "@/helpers/searchLimit";
	import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
	import EditUserDialog from "@/components/EditUserDialog.vue";
	import { User } from "@/types/User";
	import { Ref, computed, getCurrentInstance, onMounted, ref, useAttrs } from "vue";
	import api from "@/api";
	import { loadProjectShareRoster } from "@/helpers/projectShareRoster";
	import { useAsyncAutocompleteItems } from "@/composables/useAsyncAutocompleteItems";
	import { useCurrentUserRole } from "@/composables/useCurrentUserRole";
	import { AccessType } from "@/enums/AccessType";

	const attrs = useAttrs();
	const instance = getCurrentInstance();
	const { canManageUsers, isLoading: isRoleLoading } = useCurrentUserRole();
	const editUserDialog = ref<{ open: (id: string) => Promise<User | null> } | null>(null);

	const matches = (u: User, w: string) => {
		const name = (u.name || "").toLowerCase();
		const first = (u.firstname || "").toLowerCase();
		const last = (u.lastname || "").toLowerCase();
		const email = (u.email || "").toLowerCase();
		const phone = (u.phone || "").toLowerCase();
		return (
			name.includes(w) ||
			`${first} ${last}`.trim().includes(w) ||
			first.includes(w) ||
			last.includes(w) ||
			email.includes(w) ||
			phone.includes(w)
		);
	};

	const tokenize = (query: string) => query.toLowerCase().split(" ").filter(Boolean);

	// Extended from https://github.com/vuetifyjs/vuetify/blob/50b6c671dbf8223c6887cebf3ad43daf36779952/packages/vuetify/src/components/VAutocomplete/VAutocomplete.ts#L38
	const customFilter = (value: string, queryText: string, item: any) => {
		const valueLower = value.toLocaleLowerCase();
		const queryLower = queryText.toLocaleLowerCase();

		if (valueLower.indexOf(queryLower) > -1) {
			return true;
		}

		// If the default filter didn't find a match, try to match the query against the raw item.
		const user = item.raw as User;
		const words = tokenize(queryText);
		if (user && words.length > 0 && words.every((w) => matches(user, w))) {
			return true;
		}
		return false;
	};

	const props = withDefaults(
		defineProps<{
			/**
			 * When provided, restricts the user list to editors on this project
			 * (the creator, organization administrators, and non-viewer project shares), then applies client-side search filtering.
			 */
			projectId?: string;
			returnObject?: boolean;
			/**
			 * When true, fetches via getSharingUsers() instead of getUsers().
			 * Restores the original sharing-user endpoint used in ActionSchedule and ActionVideo.
			 */
			sharingOnly?: boolean;
			/**
			 * When true, restricts results to users with the "administrator" role.
			 * Only meaningful when sharingOnly is also true.
			 */
			administratorsOnly?: boolean;
		}>(),
		{
			returnObject: true,
			sharingOnly: false,
			administratorsOnly: false
		}
	);

	/**
	 * Get the selected attribute from the instance.
	 */
	function getSelectedAttr(): unknown {
		return attrs.modelValue ?? attrs["model-value"] ?? attrs.value;
	}

	const isMultiple = computed<boolean>(() => attrs.multiple === true);
	const selected = computed<any>(() => getSelectedAttr());
	const isDisabled = computed<boolean>(() => !!attrs.disabled);
	// Editing a user opens settings restricted to admins (see the Users route's
	// `requiredRoles`). Gate the edit icon on the admin role set so the User role
	// can't open an editor it isn't allowed to save (LSP-16351). `isRoleLoading`
	// avoids flashing the icon before /me resolves.
	const canEditSelected = computed<boolean>(
		() =>
			!isRoleLoading.value &&
			canManageUsers.value &&
			!isDisabled.value &&
			!isMultiple.value &&
			!!selected.value &&
			!!selected.value.id
	);

	/**
	 * Resolve the caller's own organization id for the project assignee picker's
	 * organization-administrator gate. `api.getMe()` caches its response and deduplicates
	 * concurrent calls (see `useCurrentUserRole`'s note), so this costs no extra request once the
	 * role composable has resolved. When it can't be resolved the gate is simply not applied
	 * rather than emptying the picker of otherwise valid editors.
	 */
	async function fetchCallerOrganizationId(): Promise<string | undefined> {
		try {
			const me = await api.getMe();
			return me?.organization?.id;
		} catch {
			return undefined;
		}
	}

	const { items, isLoading, searchTerm, onSearchInput, refresh } = useAsyncAutocompleteItems<User>({
		fetchItems: async (term: string) => {
			// If a projectId is provided, load editors from the creator, organization administrators, and ProjectShares, then filter client-side.
			if (props.projectId) {
				type ProjectShare = { User?: User; accessType: string };
				// LSP-20532: 404 (project owned by another organization) resolves to an empty roster instead
				// of escaping the autocomplete as an uncaught rejection.
				const [resp, project, sharingUsers, callerOrganizationId] = await Promise.all([
					loadProjectShareRoster(props.projectId, { pageSize: 1000 }),
					api.getProject(props.projectId),
					api.getSharingUsers(),
					fetchCallerOrganizationId()
				]);
				// architecture-gap round 65: project.User (eager-loaded by getProject) is
				// cross-org-safe, unlike api.getUsers which is scoped to the caller's own
				// organization's accounts and returns zero rows for a cross-org creator.
				const creatorResponse = project?.User
					? { rows: [project.User] }
					: project?.createdBy
						? await api.getUsers({
								page: 1,
								pageSize: 1,
								where: { id: project.createdBy }
							})
						: { rows: [] };

				// Administrators are EDITORs only on projects their OWN organization owns:
				// contact-api's resolveProjectAccessContext reaches its org-admin arm only inside
				// `belongsToSameOrganization`, and the server project verdict and
				// mobile's ProjectShare.kt gate the same way. `api.getSharingUsers()` is scoped
				// server-side to the CALLER's organization, so for a project reached through a
				// cross-organization share those administrators hold no grant on it at all and must not
				// be offered as assignees.
				// When either organization id is unavailable the comparison can't be made, so the gate is
				// skipped rather than hiding editors the caller legitimately has.
				const projectOrganizationId = project?.organizationId;
				const includeOrganizationAdministrators =
					!callerOrganizationId || !projectOrganizationId || projectOrganizationId === callerOrganizationId;
				const organizationAdministrators: User[] = includeOrganizationAdministrators
					? sharingUsers.filter((u: any) =>
							u.Accounts?.[0]?.Roles?.some((role: any) => role?.name?.toLowerCase() === "administrator")
						)
					: [];

				const members: User[] = [
					...(creatorResponse.rows as User[]),
					...organizationAdministrators,
					...((resp?.rows || []) as ProjectShare[])
						.filter((x: ProjectShare) => x.User && x.accessType !== AccessType.Viewer)
						.map((x: ProjectShare) => x.User as User)
				];

				const words = tokenize(term);
				if (words.length === 0) {
					// Return unique members by id
					const unique = new Map<string, User>();
					for (const u of members) {
						if (!unique.has(u.id)) unique.set(u.id, u);
					}
					return Array.from(unique.values());
				}

				const filtered = members.filter((u) => words.every((w) => matches(u, w)));
				// Dedupe by id
				const uniq = new Map<string, User>();
				for (const u of filtered) {
					if (!uniq.has(u.id)) uniq.set(u.id, u);
				}
				return Array.from(uniq.values());
			}

			// Sharing users: fetch via the sharing endpoint, optionally restricted to administrators
			if (props.sharingOnly) {
				const result: User[] = await api.getSharingUsers();
				const filtered = props.administratorsOnly
					? result.filter((u: any) => u.Accounts?.[0]?.Roles?.[0]?.name?.toLowerCase() === "administrator")
					: result;
				if (!term) return filtered;
				const words = tokenize(term);
				return filtered.filter((u) => words.every((w) => matches(u, w)));
			}

			// Default behavior: server-side search across users
			const words = tokenize(term);
			const or: any[] = [];
			for (const word of words) {
				or.push({ firstname: { $ilike: `%${word}%` } });
				or.push({ lastname: { $ilike: `%${word}%` } });
				or.push({ email: { $ilike: `%${word}%` } });
				or.push({ phone: { $ilike: `%${word}%` } });
			}
			const resp = await api.getUsers({
				page: 1,
				pageSize: 1000,
				where: or.length ? { $or: or } : {}
			});
			return resp.rows as User[];
		},

		getSelected: () => {
			return getSelectedAttr() as User | User[] | string | string[] | undefined;
		},

		fetchByIds: async (ids: string[]) => {
			if (ids.length === 0) {
				return [];
			}
			const resp = await api.getUsers({
				page: 1,
				pageSize: ids.length,
				where: { id: { $in: ids } }
			});
			return resp.rows as User[];
		}
	});

	const listOfTeamMembers = items as Ref<User[]>;

	onMounted(() => {
		const sel = getSelectedAttr();
		if (sel) {
			refresh();
		}
	});

	const getUserDisplayName = (user: User) => {
		if (user.name) {
			return user.name;
		} else if (user.firstname && user.lastname) {
			return user.firstname + " " + user.lastname;
		} else if (user.phone) {
			return user.phone;
		} else if (user.email) {
			return user.email;
		}
		return "";
	};

	async function handleEditClick() {
		if (!canEditSelected.value || !editUserDialog.value) return;
		const userId = selected.value.id as string;
		const updated = await editUserDialog.value.open(userId);
		if (updated) {
			// Update the item in the local list so the display reflects changes immediately
			const idx = items.value.findIndex((u) => u.id === updated.id);
			if (idx !== -1) {
				items.value = [...items.value.slice(0, idx), updated, ...items.value.slice(idx + 1)];
			} else {
				items.value = [...items.value, updated];
			}
			(instance as any)?.proxy?.$emit("update:modelValue", updated);
		}
	}
</script>
<style scoped></style>
